/**
 * assets/order-history.js
 *
 * Handles the Order History page:
 *  - Keyword search (filters rows in real-time)
 *  - Select-all / per-row checkbox management
 *  - Dynamic "Add to Cart" button state
 *  - Bulk add-to-cart via Shopify /cart/add.js API
 *  - Quantity recalculation for Ext. Price display
 */

(function () {
  'use strict';

  // ─── DOM refs ────────────────────────────────────────────────────────────────
  const keywordInput   = document.getElementById('oh-keyword');
  const searchBtn      = document.getElementById('oh-search-btn');
  const addToCartBtn   = document.getElementById('oh-add-to-cart-btn');
  const selectAllChk   = document.getElementById('oh-select-all');
  const successMsg     = document.getElementById('oh-success');
  const errorMsg       = document.getElementById('oh-error');

  // Exit gracefully if the section is not on this page (e.g. logged-out state)
  if (!keywordInput || !addToCartBtn) return;

  /** @returns {HTMLTableRowElement[]} */
  function allRows() {
    return Array.from(document.querySelectorAll('#oh-tbody .oh-row'));
  }

  /** @returns {HTMLTableRowElement[]} */
  function visibleRows() {
    return allRows().filter((row) => !row.dataset.hidden);
  }

  // ─── Search / filter ─────────────────────────────────────────────────────────

  function applySearch() {
    const keyword = (keywordInput.value || '').trim().toLowerCase();
    const rows    = allRows();

    /**
     * When filtering we need to handle the grouped date/order columns:
     * The first row of each order group shows the date + order link.
     * If it gets hidden we must promote the date/order to the next visible sibling.
     * Strategy: track groups by order name, then re-stamp dates.
     */

    // Step 1 – mark hidden
    rows.forEach((row) => {
      const searchText = (row.dataset.search || '').toLowerCase();
      const matches    = !keyword || searchText.includes(keyword);
      if (matches) {
        delete row.dataset.hidden;
      } else {
        row.dataset.hidden = '1';
      }
    });

    // Step 2 – re-stamp date/order columns within each order group
    const seen = new Set();
    rows.forEach((row) => {
      const orderName = row.dataset.orderName;
      if (row.dataset.hidden) return;

      if (!seen.has(orderName)) {
        seen.add(orderName);
        // Make sure the date/order cells are populated for the first visible row
        const dateCell  = row.querySelector('.oh-cell-date');
        const orderCell = row.querySelector('.oh-cell-order');
        if (dateCell && !dateCell.textContent.trim()) {
          dateCell.textContent = row.dataset.orderDate || '';
        }
        if (orderCell && !orderCell.innerHTML.trim()) {
          // Re-render the order link from data attrs
          const name     = orderName;
          // We cannot recover the URL from data attrs without embedding it; use # as fallback
          const linkEl   = document.querySelector(
            `.oh-row[data-order-name="${CSS.escape(orderName)}"] .oh-order-link`
          );
          if (linkEl) {
            orderCell.innerHTML = linkEl.parentElement.outerHTML.replace(/<td[^>]*>|<\/td>/gi, '');
          } else {
            orderCell.textContent = name;
          }
        }
      } else {
        // Subsequent visible rows in this group should NOT show date/order
        const dateCell  = row.querySelector('.oh-cell-date');
        const orderCell = row.querySelector('.oh-cell-order');
        if (dateCell)  dateCell.textContent  = '';
        if (orderCell) orderCell.innerHTML   = '';
      }
    });

    // Step 3 – uncheck hidden rows
    rows.forEach((row) => {
      if (row.dataset.hidden) {
        const chk = row.querySelector('.oh-row-check');
        if (chk) chk.checked = false;
      }
    });

    updateSelectAll();
    updateAddToCartState();
  }

  searchBtn.addEventListener('click', applySearch);
  keywordInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') applySearch();
  });
  // Clear search on empty
  keywordInput.addEventListener('input', () => {
    if (!keywordInput.value) applySearch();
  });

  // ─── Checkbox management ──────────────────────────────────────────────────────

  function checkedRows() {
    return visibleRows().filter((row) => {
      const chk = row.querySelector('.oh-row-check');
      return chk && chk.checked;
    });
  }

  function updateAddToCartState() {
    const hasSelection = checkedRows().length > 0;
    addToCartBtn.disabled = !hasSelection;
  }

  function updateSelectAll() {
    const visible = visibleRows();
    const checked = visible.filter((row) => {
      const chk = row.querySelector('.oh-row-check');
      return chk && chk.checked;
    });
    if (checked.length === 0) {
      selectAllChk.checked       = false;
      selectAllChk.indeterminate = false;
    } else if (checked.length === visible.length) {
      selectAllChk.checked       = true;
      selectAllChk.indeterminate = false;
    } else {
      selectAllChk.checked       = false;
      selectAllChk.indeterminate = true;
    }
  }

  // Delegate checkbox change from tbody
  document.getElementById('oh-tbody')?.addEventListener('change', (e) => {
    if (e.target.classList.contains('oh-row-check')) {
      updateSelectAll();
      updateAddToCartState();
    }
  });

  selectAllChk.addEventListener('change', () => {
    const shouldCheck = selectAllChk.checked;
    visibleRows().forEach((row) => {
      const chk = row.querySelector('.oh-row-check');
      if (chk) chk.checked = shouldCheck;
    });
    updateAddToCartState();
  });

  // ─── Quantity → Ext. Price live recalculation ─────────────────────────────────

  document.getElementById('oh-tbody')?.addEventListener('input', (e) => {
    if (e.target.classList.contains('oh-qty-input')) {
      // We don't have unit price stored, so we skip live price recalc.
      // Merchants can extend this by embedding data-unit-price on each row.
    }
  });

  // ─── Add to Cart ──────────────────────────────────────────────────────────────

  function showMessage(type, text) {
    successMsg.classList.remove('visible');
    errorMsg.classList.remove('visible');
    const el = type === 'success' ? successMsg : errorMsg;
    el.textContent = text;
    el.classList.add('visible');
    setTimeout(() => el.classList.remove('visible'), 5000);
  }

  /**
   * POST /cart/add.js with multiple items.
   * @param {{ id: number, quantity: number }[]} items
   */
  async function addItemsToCart(items) {
    const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.description || `Error ${response.status}`);
    }

    return response.json();
  }

  addToCartBtn.addEventListener('click', async () => {
    const selected = checkedRows();
    if (!selected.length) return;

    const items = selected
      .map((row) => {
        const variantId = parseInt(row.dataset.variantId, 10);
        const qtyInput  = row.querySelector('.oh-qty-input');
        const qty       = Math.max(1, parseInt(qtyInput?.value || '1', 10));
        if (!variantId || isNaN(variantId)) return null;
        return { id: variantId, quantity: qty };
      })
      .filter(Boolean);

    if (!items.length) {
      showMessage('error', 'No valid products selected.');
      return;
    }

    addToCartBtn.disabled    = true;
    addToCartBtn.textContent = 'Adding…';

    try {
      await addItemsToCart(items);

      // Notify theme cart components (drawer, icon counter, etc.)
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));

      showMessage('success', `${items.length} item(s) added to your cart.`);

      // Uncheck selected rows
      selected.forEach((row) => {
        const chk = row.querySelector('.oh-row-check');
        if (chk) chk.checked = false;
      });
      updateSelectAll();
    } catch (err) {
      showMessage('error', err.message || 'Could not add items to cart. Please try again.');
    } finally {
      addToCartBtn.textContent = 'Add to Cart';
      updateAddToCartState();
    }
  });
})();
